import { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import ModalPortal from "../ModalPortal";

// Visor de fotos a pantalla completa con tira de miniaturas abajo.
// Flechas / teclado / swipe para navegar; click en una miniatura salta a esa foto.
//
// Props:
// - images: array de URLs
// - startIndex: foto con la que abre
// - onClose: callback al cerrar (Escape, X o click en el fondo)
function ImageLightbox({ images = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const thumbsRef = useRef(null);
  const touchStartX = useRef(null);

  const goTo = useCallback(
    (i) => setIndex(Math.max(0, Math.min(images.length - 1, i))),
    [images.length]
  );

  // Teclado: flechas para navegar, Escape para cerrar
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goTo(index - 1);
      if (e.key === "ArrowRight") goTo(index + 1);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [index, goTo, onClose]);

  // Bloquear el scroll de la página mientras el visor está abierto
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Mantener la miniatura activa visible dentro de la tira
  useEffect(() => {
    const strip = thumbsRef.current;
    const active = strip?.children[index];
    if (active) {
      active.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [index]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    goTo(delta < 0 ? index + 1 : index - 1);
  };

  if (!images.length) return null;

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
        {/* Barra superior: contador + cerrar */}
        <div className="flex items-center justify-between px-4 py-3 text-white flex-shrink-0">
          <span className="text-sm font-medium text-white/80">
            {index + 1} / {images.length}
          </span>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Imagen principal */}
        <div
          className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16"
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={images[index]}
            alt={`Foto ${index + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />

          {index > 0 && (
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={(e) => {
                e.stopPropagation();
                goTo(index - 1);
              }}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
          )}
          {index < images.length - 1 && (
            <button
              type="button"
              aria-label="Foto siguiente"
              onClick={(e) => {
                e.stopPropagation();
                goTo(index + 1);
              }}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tira de miniaturas */}
        {images.length > 1 && (
          <div className="flex-shrink-0 py-3 px-2">
            <div
              ref={thumbsRef}
              className="flex gap-2 overflow-x-auto justify-start sm:justify-center max-w-full mx-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Ver foto ${i + 1}`}
                  onClick={() => goTo(i)}
                  className={`flex-shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    i === index
                      ? "border-white opacity-100"
                      : "border-transparent opacity-50 hover:opacity-80"
                  }`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </ModalPortal>
  );
}

export default ImageLightbox;
